import type { ManifestObjectAppearance } from "daedalus-core";
import { TICK_MS } from "../movement/step.ts";

/**
 * How long a thing's change of state takes (ADR-058's amendment of 2026-10-07): the length of the one-shot strip the
 * compiled appearance carries for that pair of states. The map server holds an uninterruptible thing for it and the
 * page plays the same strip, so both read the same numbers and neither reads a constant.
 */

/**
 * The length of the strip an appearance plays for a change between two states.
 *
 * @param look The thing's appearance, from the running version's manifest.
 * @param from The index of the state left.
 * @param to The index of the state taken.
 * @returns The sum of the strip's durations in milliseconds, or 0 when the appearance carries no strip for the pair,
 *   which is a cut.
 */
export function transitionMs(look: ManifestObjectAppearance, from: number, to: number): number {
  const transitions = look.transitions;
  if (transitions === undefined) {
    return 0;
  }
  for (let index = 0; index < transitions.length; index += 1) {
    const transition = transitions[index] as NonNullable<ManifestObjectAppearance["transitions"]>[number];
    if (transition.from === from && transition.to === to) {
      let total = 0;
      for (let frame = 0; frame < transition.durations.length; frame += 1) {
        total += transition.durations[frame] as number;
      }
      return total;
    }
  }
  return 0;
}

/**
 * The same length in ticks, rounded up, so a thing held for it is held until the whole strip has run.
 *
 * @param look The thing's appearance.
 * @param from The index of the state left.
 * @param to The index of the state taken.
 * @returns The ticks, 0 for a cut.
 */
export function transitionTicks(look: ManifestObjectAppearance, from: number, to: number): number {
  return Math.ceil(transitionMs(look, from, to) / TICK_MS);
}
